import { useMemo } from 'react';
import { ChoiceDrill } from './ChoiceDrill';
import type { DrillProps } from './meta';
import { makeSpaceRound, type Cell, type PaperFrame } from './space';

const U = 10; // 한 칸 크기

function Poly({ cells, label }: { cells: Cell[]; label: string }) {
  const w = Math.max(...cells.map((c) => c[0])) + 1, h = Math.max(...cells.map((c) => c[1])) + 1;
  return (
    <svg className="poly" viewBox={`-1 -1 ${w * U + 2} ${h * U + 2}`} role="img" aria-label={label} style={{ aspectRatio: `${w * U + 2} / ${h * U + 2}` }}>
      {cells.map(([x, y]) => <rect key={`${x},${y}`} x={x * U} y={y * U} width={U} height={U} className="poly-cell" />)}
    </svg>
  );
}

function Pieces({ set }: { set: Cell[][] }) {
  let x = 0;
  const placed = set.map((p) => {
    const w = Math.max(...p.map((c) => c[0])) + 1;
    const at = x;
    x += w + 1;
    return { p, at };
  });
  const h = Math.max(...set.flatMap((p) => p.map((c) => c[1]))) + 1;
  const W = (x - 1) * U + 2, H = h * U + 2;
  return (
    <svg className="poly pieces" viewBox={`-1 -1 ${W} ${H}`} role="img" aria-label={`조각 ${set.length}개`} style={{ aspectRatio: `${W} / ${H}` }}>
      {placed.map(({ p, at }, i) => p.map(([cx, cy]) => <rect key={`${i}-${cx},${cy}`} x={(at + cx) * U} y={cy * U} width={U} height={U} className={`poly-cell piece-${i}`} />))}
    </svg>
  );
}

const G = 4, S = 12; // 종이 칸 수, 칸 크기
function HoleGrid({ holes, label }: { holes: Cell[]; label: string }) {
  return (
    <svg className="paper" viewBox={`-2 -2 ${G * S + 4} ${G * S + 4}`} role="img" aria-label={label}>
      <rect x="0" y="0" width={G * S} height={G * S} className="paper-sheet" />
      {Array.from({ length: G - 1 }, (_, i) => (
        <g key={i}>
          <line x1={(i + 1) * S} y1="0" x2={(i + 1) * S} y2={G * S} className="paper-grid" />
          <line x1="0" y1={(i + 1) * S} x2={G * S} y2={(i + 1) * S} className="paper-grid" />
        </g>
      ))}
      {holes.map(([x, y]) => <circle key={`${x},${y}`} cx={x * S + S / 2} cy={y * S + S / 2} r={S / 4} className="paper-hole" />)}
    </svg>
  );
}

function FoldFrame({ f, holes, step }: { f: PaperFrame; holes?: Cell[]; step: number }) {
  const [x0, y0, x1, y1] = f.region;
  const vertical = f.fold === 'L' || f.fold === 'R';
  const a = (f.axis ?? 0) * S;
  // 화살표: 접히는 쪽 가운데 → 접히는 선 너머
  const mid = (u0: number, u1: number) => ((u0 + u1) / 2) * S;
  let arrow: [number, number, number, number] | null = null;
  if (f.fold === 'L') arrow = [mid(x0, f.axis!), mid(y0, y1), a + (a - mid(x0, f.axis!)), mid(y0, y1)];
  if (f.fold === 'R') arrow = [mid(f.axis!, x1), mid(y0, y1), a - (mid(f.axis!, x1) - a), mid(y0, y1)];
  if (f.fold === 'T') arrow = [mid(x0, x1), mid(y0, f.axis!), mid(x0, x1), a + (a - mid(y0, f.axis!))];
  if (f.fold === 'B') arrow = [mid(x0, x1), mid(f.axis!, y1), mid(x0, x1), a - (mid(f.axis!, y1) - a)];
  return (
    <figure className="fold-frame">
      <svg className="paper" viewBox={`-2 -2 ${G * S + 4} ${G * S + 4}`} role="img" aria-label={f.fold ? `${step}단계 접기 ${f.fold}` : '구멍 뚫기'}>
        <defs>
          <marker id={`ah${step}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" className="paper-arrowhead" />
          </marker>
        </defs>
        <rect x="0" y="0" width={G * S} height={G * S} className="paper-ghost" />
        <rect x={x0 * S} y={y0 * S} width={(x1 - x0) * S} height={(y1 - y0) * S} className="paper-sheet" />
        {f.fold && (vertical
          ? <line x1={a} y1={y0 * S - 2} x2={a} y2={y1 * S + 2} className="paper-axis" />
          : <line x1={x0 * S - 2} y1={a} x2={x1 * S + 2} y2={a} className="paper-axis" />)}
        {arrow && <line x1={arrow[0]} y1={arrow[1]} x2={arrow[2]} y2={arrow[3]} className="paper-arrow" markerEnd={`url(#ah${step})`} />}
        {holes?.map(([x, y]) => <circle key={`${x},${y}`} cx={x * S + S / 2} cy={y * S + S / 2} r={S / 4} className="paper-hole" />)}
      </svg>
      <figcaption>{f.fold ? `${step}. 접기` : `${step}. 구멍`}</figcaption>
    </figure>
  );
}

function Net({ net }: { net: { cell: Cell; sym: string }[] }) {
  const w = Math.max(...net.map((f) => f.cell[0])) + 1, h = Math.max(...net.map((f) => f.cell[1])) + 1;
  const N = 16;
  return (
    <svg className="net" viewBox={`-1 -1 ${w * N + 2} ${h * N + 2}`} role="img" aria-label="정육면체 전개도" style={{ aspectRatio: `${w * N + 2} / ${h * N + 2}` }}>
      {net.map(({ cell: [x, y], sym }) => (
        <g key={`${x},${y}`}>
          <rect x={x * N} y={y * N} width={N} height={N} className="net-face" />
          <text x={x * N + N / 2} y={y * N + N / 2 + 4} textAnchor="middle" className="net-sym">{sym}</text>
        </g>
      ))}
    </svg>
  );
}

export function SpaceDrill({ level, seed, onDone }: DrillProps) {
  const questions = useMemo(() => makeSpaceRound(level, seed), [level, seed]);
  return (
    <ChoiceDrill
      questions={questions} seed={seed} onDone={onDone} className="space-drill"
      info={(q) => ({ kind: q.kind })}
      choicesClass={(q) => (q.kind === 'net' ? '' : 'figs')}
      render={(q) => (
        <div className="space-q">
          <p className="lang-ask small-ask">{q.ask}</p>
          <div className="space-stem">
            {q.kind === 'rotate' && <Poly cells={q.shape} label="기준 모양" />}
            {q.kind === 'pieces' && <Poly cells={q.shape} label="채울 모양" />}
            {q.kind === 'paper' && q.frames.map((f, i) => <FoldFrame key={i} f={f} step={i + 1} holes={f.fold ? undefined : q.holes} />)}
            {q.kind === 'net' && <Net net={q.net} />}
          </div>
        </div>
      )}
      renderChoice={(q, i) => {
        if (q.kind === 'rotate') return <Poly cells={q.options[i]} label={`보기 ${i + 1}`} />;
        if (q.kind === 'pieces') return <Pieces set={q.options[i]} />;
        if (q.kind === 'paper') return <HoleGrid holes={q.options[i]} label={`보기 ${i + 1}`} />;
        return <span className="choice-text net-opt">{q.options[i]}</span>;
      }}
    />
  );
}
